/*
# -----------------------------------------------------------------------------
# Component: commonAjaxMixin.js
# Author: antonio.ramirez@pst.sg
# Created Date: 28/May/2019
# Comments: Responsible for all common backend related calls for Manage Accounts
# -----------------------------------------------------------------------------
*/
import MessageMixin from '@/mixins/message'
import { expandURI, filterQuery } from '@/utils/ajax'
import { extractMenu } from '@/utils/extract'
import clone from 'clone'
import { formatDisplayDateTime2 } from '@/utils/date'

export const commonAjaxMixin = {
  data() {
    return {
      commonName: undefined,
      bankHelpListScreen: '',
      rptMenuLink: {},
      paytMenuLink: {},
      drNoteMenuLink: {},
      crNoteMenuLink: {},
      sanctionName1Error: '',
      sanctionName2Error: '',
      sanctionName3Error: '',
      sanctionDocNoError: '',
      sanctionPartialMatchItems: [],
      showSanctionDialog: false,
      paginationPolicyRenewal: {
        sortDesc: [false],
        page: 1,
        itemsPerPage: 5
      },
      promptMsg: false,
      errMsg: '',
      isWarning: false,
      acceptInterWarn: false
    }
  },
  computed: {
    acctMenu() {
      return this.$store.state.reports.acctReports.menu
    }
  },
  watch: {
    'sanctionPartialMatchItems': function (value) {
      if (value && value.length > 0) {
        this.showSanctionDialog = true
      }
    },
    'paginationPolicyRenewal': function (value) {
      this.fetchPolicyRenewalData()
    }
  },
  methods: {
    async getAcctPeriods(modNo) {
      const apiUrl = this.$store.state.commons.menu.acctPeriodList
      const expand = {
        modNo: modNo
      }
      if (apiUrl) {
        const result = await this.$http.get(expandURI(apiUrl, expand))
        if (result.data) {
          this.$set(this, 'acctPeriodList', result.data.data)
        }
      }
    },
    async getBranchList() {
      const apiUrl = this.$store.state.commons.menu.branchList
      if (apiUrl) {
        const result = await this.$http.get(apiUrl)
        if (result.data.branchList) {
          this.$set(this, 'branches', result.data.branchList)
        }
      }
    },
    async getBranches(elemName) {
      const apiUrl = this.$store.state.commons.menu.branchList
      if (apiUrl) {
        const result = await this.$http.get(apiUrl)
        if (result.data.branchList) {
          for (const branch of result.data.branchList) {
            branch.label = branch.value + ' - ' + branch.label
          }
          this.$set(this, elemName, result.data.branchList)
        }
      }
    },
    async fetchAcctNoData() {
      const apiUrl = this.$store.state.commons.menu.acctNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationAcctNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersAcctNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationAcctNo.sortBy, this.paginationAcctNo.sortDesc))
          if (result.data) {
            this.totalItemsAcctNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'acctNoItems', data)
            } else {
              this.$set(this, 'acctNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchBillAcctNoData() {
      const apiUrl = this.$store.state.commons.menu.billAcctNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationBillAcctNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersBillAcctNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationBillAcctNo.sortBy, this.paginationBillAcctNo.sortDesc))
          if (result.data) {
            this.totalItemsBillAcctNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'billAcctNoItems', data)
            } else {
              this.$set(this, 'billAcctNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchGlAcctNoData() {
      const apiUrl = this.$store.state.commons.menu.glAcctNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationGlAcctNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersGlAcctNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationGlAcctNo.sortBy, this.paginationGlAcctNo.sortDesc))
          if (result.data) {
            this.totalItemsGlAcctNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'glAcctNoItems', data)
            } else {
              this.$set(this, 'glAcctNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchContraDocNoData() {
      const apiUrl = this.$store.state.commons.menu.contraDocNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationContraDocNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersContraDocNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationContraDocNo.sortBy, this.paginationContraDocNo.sortDesc))
          if (result.data) {
            this.totalItemsContraDocNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'contraDocNoItems', data)
            } else {
              this.$set(this, 'contraDocNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchOvrdAcctNoData() {
      const apiUrl = this.$store.state.commons.menu.ovrdAcctNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationOvrdAcctNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersOvrdAcctNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationOvrdAcctNo.sortBy, this.paginationOvrdAcctNo.sortDesc))
          if (result.data) {
            this.totalItemsOvrdAcctNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'ovrdAcctNoItems', data)
            } else {
              this.$set(this, 'ovrdAcctNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchFundData() {
      const apiUrl = this.$store.state.commons.menu.fundList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationFund
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersFund)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationFund.sortBy, this.paginationFund.sortDesc))
          if (result.data) {
            this.totalItemsFund = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'fundItems', data)
            } else {
              this.$set(this, 'fundItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchPolicyData() {
      const apiUrl = this.$store.state.commons.menu.policyList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationPolicy
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersPolicy)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationPolicy.sortBy, this.paginationPolicy.sortDesc))
          if (result.data) {
            this.totalItemsPol = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'polItems', data)
            } else {
              this.$set(this, 'polItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchClmData() {
      const apiUrl = this.$store.state.commons.menu.clmList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationClm
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersClm)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationClm.sortBy, this.paginationClm.sortDesc))
          if (result.data) {
            this.totalItemsClm = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'clmItems', data)
            } else {
              this.$set(this, 'clmItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchAllClmData() {
      const apiUrl = this.$store.state.commons.menu.allClmList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationClm
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersClm)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationClm.sortBy, this.paginationClm.sortDesc))
          if (result.data) {
            this.totalItemsClm = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'clmItems', data)
            } else {
              this.$set(this, 'clmItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchClmEventData() {
      const apiUrl = this.$store.state.commons.menu.clmEventList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationClm
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filters)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationClm.sortBy, this.paginationClm.sortDesc))
          if (result.data) {
            this.totalItemsClmEvent = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'clmEventItems', data)
            } else {
              this.$set(this, 'clmEventItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchClmStatData() {
      const apiUrl = this.$store.state.commons.menu.clmStatList
      if (apiUrl) {
        try {
          this.loading = true
          const result = await this.$http.get(apiUrl)
          if (result.data) {
            this.totalItemsClmEvent = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'clmStatItems', data)
            } else {
              this.$set(this, 'clmStatItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchDocNoData(transaction, action, callerPage) {
      const apiUrl = this.$store.state.commons.menu.docNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationDocNo
          const expand = {
            page: page - 1,
            size: itemsPerPage,
            transactions: transaction,
            actions: action,
            callerPage: callerPage
          }
          let massageFilters = clone(this.filtersDocNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationDocNo.sortBy, this.paginationDocNo.sortDesc))
          if (result.data) {
            this.totalItemsDocNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'docNoItems', data)
            } else {
              this.$set(this, 'docNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchDocNoHelpList(pageName) {
      const apiUrl = this.$store.state.commons.menu.docNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationDocNo
          const expand = {
            page: page - 1,
            size: itemsPerPage,
            callerPage: pageName
          }
          let massageFilters = clone(this.filtersDocNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationDocNo.sortBy, this.paginationDocNo.sortDesc))
          if (result.data) {
            this.totalItemsDocNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'docNoItems', data)
            } else {
              this.$set(this, 'docNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchClmDocData() {
      const apiUrl = this.$store.state.commons.menu.clmDocList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationDocNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersClmDocNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationDocNo.sortBy, this.paginationDocNo.sortDesc))
          if (result.data) {
            this.totalItemsDocNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'clmDocItems', data)
            } else {
              this.$set(this, 'clmDocItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchCurrData() {
      // const apiUrl = this.$store.state.commons.menu.currList
      const apiUrl = this.$store.state.commons.menu.currRateList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationCurr
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersCurr)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationCurr.sortBy, this.paginationCurr.sortDesc))
          if (result && result.data) {
            this.totalItemsCurr = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'currItems', data)
            } else {
              this.$set(this, 'currItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchAcctSufData() {
      const apiUrl = this.$store.state.commons.menu.acctSufList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationAcctSuf
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersAcctSuf)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationAcctSuf.sortBy, this.paginationAcctSuf.sortDesc))
          if (result.data) {
            this.totalItemsAcctSuf = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'acctSufItems', data)
            } else {
              this.$set(this, 'acctSufItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchPayersChequeNoData() {
      const apiUrl = this.$store.state.commons.menu.payersChequeNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationPayersChequeNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersPayersChequeNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationPayersChequeNo.sortBy, this.paginationPayersChequeNo.sortDesc))
          if (result.data) {
            this.totalItemsPayersChequeNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'payersChequeNoItems', data)
            } else {
              this.$set(this, 'payersChequeNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchBankNoData() {
      const apiUrl = this.$store.state.commons.menu.bankList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationBankNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersBankNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationBankNo.sortBy, this.paginationBankNo.sortDesc))
          if (result.data) {
            this.totalItemsBankNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'bankNoItems', data)
            } else {
              this.$set(this, 'bankNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchAddrToData() {
      const apiUrl = this.$store.state.commons.menu.addrToList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationAddrTo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersAddrTo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationAddrTo.sortBy, this.paginationAddrTo.sortDesc))
          if (result.data) {
            this.totalItemsAddrTo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'addrToItems', data)
            } else {
              this.$set(this, 'addrToItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchBranchData() {
      const apiUrl = this.$store.state.commons.menu.branchesList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationBranch
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersBranch)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationBranch.sortBy, this.paginationBranch.sortDesc))
          if (result.data) {
            this.totalItemsBranch = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'branchItems', data)
            } else {
              this.$set(this, 'branchItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchCrDocNoData() {
      const apiUrl = this.$store.state.commons.menu.crDocNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationDocNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }

          let massageFilters = clone(this.filtersDocNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationDocNo.sortBy, this.paginationDocNo.sortDesc))
          if (result.data) {
            this.totalItemsDocNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'docNoItems', data)
            } else {
              this.$set(this, 'docNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchDbDocNoData() {
      const apiUrl = this.$store.state.commons.menu.dbDocNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationDocNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }

          let massageFilters = clone(this.filtersDocNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationDocNo.sortBy, this.paginationDocNo.sortDesc))
          if (result.data) {
            this.totalItemsDocNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'docNoItems', data)
            } else {
              this.$set(this, 'docNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchCrAcctNoData() {
      const apiUrl = this.$store.state.commons.menu.crAcctNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationAcctNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersAcctNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationAcctNo.sortBy, this.paginationAcctNo.sortDesc))
          if (result.data) {
            this.totalItemsAcctNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'acctNoItems', data)
            } else {
              this.$set(this, 'acctNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchPaymtDocNoData() {
      const apiUrl = this.$store.state.commons.menu.paymtDocNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationDocNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }

          let massageFilters = clone(this.filtersDocNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationDocNo.sortBy, this.paginationDocNo.sortDesc))
          if (result.data) {
            this.totalItemsDocNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'docNoItems', data)
            } else {
              this.$set(this, 'docNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    // Res-usable method using CommonServicesController.java
    // e.g. getCommonDataList('currencies')
    async getCommonDataList(listName, paramString) {
      // Note listName is the v-model name of element object
      const apiUrl = this.$store.state.commons.menu.commonDataList
      if (apiUrl) {
        const params = {
          listName: listName,
          paramString: paramString
        }
        const result = await this.$http.get(expandURI(apiUrl, params))
        if (result.data.dataList) {
          this.$set(this, listName, result.data.dataList)
        }
      }
    },
    async getCommonDataListDropDown(listName, paramString) {
      // Note listName is the v-model name of element object
      const apiUrl = this.$store.state.commons.menu.commonDataListDropDown
      if (apiUrl) {
        const params = {
          listName: listName,
          paramString: paramString
        }
        const result = await this.$http.get(expandURI(apiUrl, params))
        if (result.data.dataList) {
          this.$set(this, listName, result.data.dataList)
        }
      }
    },
    // Re-usable method using CommonServicesController.java
    // e.g. getCommonData('PaymentMethod', 'Cash', '2020-01-01')
    async getCommonData(dataName, paramString, effDate) {
      if (this.$store.state.commons.init === 0) {
        await Promise.all([this.$store.dispatch('commonsInit')])
      }
      const apiUrl = this.$store.state.commons.menu.commonData
      if (apiUrl) {
        try {
          const params = {
            dataName: dataName,
            paramString: paramString,
            paramDate: effDate
          }
          const response = await this.$http.get(expandURI(apiUrl, params))
          const { data } = response.data
          if (data) {
            this.$set(this, dataName, data)
            if (dataName === 'accountdata') {
              this.assignAcctInfo(data)
            } else if (dataName === 'bankdata') {
              this.assignBankInfo(data)
            } else if (dataName === 'amountcurrencydata') {
              this.assignCurrencyInfo(data)
            } else if (dataName === 'acctnosuffixdata') {
              this.assignAcctSufInfo(data)
            } else if (dataName === 'gstrate') {
              this.assignGSTRate(data)
            } else if (dataName === 'currencyrate') {
              this.assignCurrRate(data)
            } else if (dataName === 'bankcurrencyrate') {
              this.assignBankCurrRate(data)
            } else if (dataName === 'acctnosfxmap_applnint_dri') {
              this.assignAcctNoSfxMapApplIntDri(data)
            }
            return true
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            if (dataName === 'gstrate') {
              this.assignGSTRate('0.00')
            }
            this.message(data)
            this.fieldMessage(data)
          }
          this.loading = false
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    /*
    async getSeqNo(seqNoType) {
      try {
        const apiUrl = this.$store.state.commons.menu.seqNo
        const params = {
          seqNoType: seqNoType
        }
        if (apiUrl) {
          this.$http.get(expandURI(apiUrl, params))
            .then((response) => {
              const { data } = response.data
              if (data) {
                this.accountsTransForm.seqNo = data
              }
            })
            .catch((error) => {
              this.$toast.error('Error retrieving sequence No which is sequence no type ' + seqNoType)
            })
        } else {
          this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        }
      } catch (error) {
        const { data } = error.response
        if (data) {
          this.message(data)
        }
      }
    },
    */
    async getSeqNo(seqNoType) {
      const apiUrl = this.$store.state.commons.menu.seqNo
      if (apiUrl) {
        try {
          const expand = {
            seqNoType: seqNoType
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result.data
          if (data) {
            this.accountsTransForm.seqNo = data
          }
        } catch (err) {
          this.$toast.error('Error retrieving sequence No which is sequence no type ' + seqNoType)
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getJrnlSeqNo() {
      const apiUrl = this.$store.state.commons.menu.jrnlSeqNo
      if (apiUrl) {
        try {
          const result = await this.$http.get(apiUrl)
          const { data } = result.data
          if (data) {
            this.accountsTransForm.seqNo = data
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchAcctPolicyData(callerPage) {
      const apiUrl = this.$store.state.commons.menu.acctPolicyList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationPolicy
          const expand = {
            page: page - 1,
            size: itemsPerPage,
            callerPage: callerPage
          }
          let massageFilters = clone(this.filtersPolicy)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationPolicy.sortBy, this.paginationPolicy.sortDesc))
          if (result.data) {
            this.totalItemsPol = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'acctPolicyItems', data)
            } else {
              this.$set(this, 'acctPolicyItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchCurrecyData() {
      const apiUrl = this.$store.state.commons.menu.currList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationCurrency
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersCurrency)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationCurrency.sortBy, this.paginationCurrency.sortDesc))
          if (result.data) {
            this.totalItemsCurrency = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'currencyItems', data)
            } else {
              this.$set(this, 'currencyItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async isDocNoExist(dataFrom) {
      const apiUrl = this.$store.state.commons.menu.checkDocNo
      if (apiUrl) {
        try {
          const params = {
            branch: dataFrom.branch,
            tranType: dataFrom.transactionType,
            docNo: dataFrom.documentNo
          }
          const response = await this.$http.get(expandURI(apiUrl, params))
          const { data } = response.data
          if (data) {
            dataFrom.isExistDocNo = data
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          return false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url')
        return false
      }
    },
    async getAcctRptMenuLink() {
      try {
        const apiURI = this.acctMenu.acctRcpt
        const result = await this.$http.get(apiURI)
        if (result.data) {
          const { data } = result.data
          if (data) {
            this.supportedFormat = data.format
            this.rptMenuLink = extractMenu(result.data._links)
          }
        }
      } catch (err) {
        const { data } = err.response
        if (data) {
          this.message(data)
        }
      }
    },
    async getAcctPaytVouMenuLink() {
      try {
        const apiURI = this.acctMenu.acctPaytVou
        const result = await this.$http.get(apiURI)
        if (result.data) {
          const { data } = result.data
          if (data) {
            this.supportedFormat = data.format
            this.paytMenuLink = extractMenu(result.data._links)
          }
        }
      } catch (err) {
        const { data } = err.response
        if (data) {
          this.message(data)
        }
      }
    },
    async getAcctDebitNoteMenuLink() {
      try {
        const apiURI = this.acctMenu.acctDrNote
        const result = await this.$http.get(apiURI)
        if (result.data) {
          const { data } = result.data
          if (data) {
            this.supportedFormat = data.format
            this.drNoteMenuLink = extractMenu(result.data._links)
          }
        }
      } catch (err) {
        const { data } = err.response
        if (data) {
          this.message(data)
        }
      }
    },
    async getAcctCreditNoteMenuLink() {
      try {
        const apiURI = this.acctMenu.acctCrNote
        const result = await this.$http.get(apiURI)
        if (result.data) {
          const { data } = result.data
          if (data) {
            this.supportedFormat = data.format
            this.crNoteMenuLink = extractMenu(result.data._links)
          }
        }
      } catch (err) {
        const { data } = err.response
        if (data) {
          this.message(data)
        }
      }
    },
    async doExportAcctDoc(param) {
      const apiURI = this.rptMenuLink.export
      const docForm = {
        branch: param.branch,
        acctNo: param.accountNo,
        tranType: param.type,
        docNo: param.documentNo,
        fund: param.fund,
        acctNoSfx: param.suffix,
        actionName: param.actionName,
        printDoc: param.printDoc
      }
      if (apiURI) {
        this.$http.post(apiURI, docForm).then(result => {
        }).catch(err => {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        })
      } else {
        this.$toast.error('An error encountered unable to print Receipt document.')
      }
    },
    async doExportAcctPaytDoc(param) {
      const apiURI = this.paytMenuLink.export
      const docForm = {
        branch: param.branch,
        acctNo: param.accountNo,
        tranType: param.type,
        docNo: param.documentNo,
        fund: param.fund,
        acctNoSfx: param.suffix,
        actionName: param.actionName,
        printDoc: param.printDoc
      }
      if (apiURI) {
        this.$http.post(apiURI, docForm).then(result => {
        }).catch(err => {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        })
      } else {
        this.$toast.error('An error encountered unable to print Payment document.')
      }
    },
    async doExportAcctDrNoteDoc(param) {
      const apiURI = this.drNoteMenuLink.export
      const docForm = {
        branch: param.branch,
        acctNo: param.accountNo,
        tranType: param.type,
        docNo: param.documentNo,
        fund: param.fund,
        acctNoSfx: param.suffix,
        actionName: param.actionName,
        printDoc: param.printDoc
      }
      if (apiURI) {
        this.$http.post(apiURI, docForm).then(result => {
        }).catch(err => {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        })
      } else {
        this.$toast.error('An error encountered unable to print Debit Note document.')
      }
    },
    async doExportAcctCrNoteDoc(param) {
      const apiURI = this.crNoteMenuLink.export
      const docForm = {
        branch: param.branch,
        acctNo: param.accountNo,
        tranType: param.type,
        docNo: param.documentNo,
        fund: param.fund,
        acctNoSfx: param.suffix,
        actionName: param.actionName,
        printDoc: param.printDoc
      }
      if (apiURI) {
        this.$http.post(apiURI, docForm).then(result => {
        }).catch(err => {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        })
      } else {
        this.$toast.error('An error encountered unable to print Credit Note document.')
      }
    },
    async getBaseCurr(paramName) {
      try {
        const apiUrl = this.$store.state.commons.menu.defaultBaseCurr
        const result = await this.$http.get(apiUrl)
        if (result.data) {
          const { data } = result.data
          if (data) {
            if (paramName.transactionType === 'Y' || paramName.transactionType === 'Z') {
              paramName.bankTotalLocalCurr = data[0]
              paramName.bankTotalLocalCurrRate = data[1]
            } else {
              paramName.netLocalAmountCurr = data[0]
            }
          }
        }
      } catch (err) {
        const { data } = err.response
        if (data) {
          this.message(data)
        }
      }
    },
    async fetchOccupationData() {
      const apiUrl = this.$store.state.commons.menu.occupationList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationOccupation
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersOccupation)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationOccupation.sortBy, this.paginationOccupation.sortDesc))
          if (result.data) {
            this.totalItemsOccupation = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'occupationItems', data)
            } else {
              this.$set(this, 'occupationItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getDefaultBankNo(branch, paramName, actionName) {
      const apiUrl = this.$store.state.commons.menu.defaultBank
      if (apiUrl) {
        try {
          const requestParams = {
            branch: branch
          }
          const response = await this.$http.get(expandURI(apiUrl, requestParams))
          const { data } = response.data
          if (data) {
            paramName.bankNo = data[0]
            if (actionName === 'receipt') {
              paramName.depositAcct = data[0]
              paramName.paymentMethod = data[4]
            } else if (actionName === 'payment') {
              paramName.fromBank = data[0]
            }
            paramName.amountCurr = data[1]
            paramName.gstAmountCurr = data[1]
            paramName.netAmountCurr = data[1]
            paramName.bankAmountCurr = data[1]
            paramName.bankChargesCurr = data[1]
            paramName.bankTotalAmountCurr = data[1]
            // this.paymentForm.bankTotalLocalCurr = data[1]
            paramName.fund = data[2]
            // this.paymentForm.accountNoSuffix = data[3]
            paramName.amountCurrRate = data[5]
            paramName.bankAmountCurrRate = data[5]
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          return false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    /*
    async ValidateTranDateRange(dataValue, paramValue) {
      const apiUrl = this.$store.state.commons.menu.validateTranDateRage
      if (dataValue.transactionType === 'Y') {
        dataValue.transactionDate = dataValue.transactionDateDisp
      }
      if (apiUrl) {
        try {
          const params = {
            tranDateType: paramValue,
            tranDateRange: formatDisplayDateTime2(dataValue.transactionDate)
          }
          const result = await this.$http.get(expandURI(apiUrl, params))
          const { data } = result.data
          if (data) {
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.loading = false
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    */
    async ValidateTranDateRange(tranDate, paramValue) {
      const apiUrl = this.$store.state.commons.menu.validateTranDateRage
      if (apiUrl) {
        try {
          const params = {
            tranDateType: paramValue,
            tranDateRange: formatDisplayDateTime2(tranDate)
          }
          const result = await this.$http.get(expandURI(apiUrl, params))
          const { data } = result.data
          if (data) {
            this.notify(data)
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.loading = false
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async fetchTreatyNoData() {
      const apiUrl = this.$store.state.commons.menu.treatyNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationTreatyNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersTreatyNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationTreatyNo.sortBy, this.paginationTreatyNo.sortDesc))
          if (result.data) {
            this.totalItemsTreatyNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'treatyNoItems', data)
            } else {
              this.$set(this, 'treatyNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchClaimTreatyNoData() {
      const apiUrl = this.$store.state.commons.menu.treatyNoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationTreatyNo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = await this.paramClaimTreatyNoData()
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationTreatyNo.sortBy, this.paginationTreatyNo.sortDesc))
          if (result.data) {
            this.totalItemsTreatyNo = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'treatyNoItems', data)
            } else {
              this.$set(this, 'treatyNoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async paramClaimTreatyNoData() {
      let messageFilters = {}
      if (this.riType.split(',').length > 1) {
        messageFilters = {
          ttyNo: this.filtersTreatyNo.ttyNo,
          ttyTypes: this.riType
        }
      } else {
        messageFilters = {
          ttyNo: this.filtersTreatyNo.ttyNo,
          ttyType: this.riType
        }
      }
      return messageFilters
    },
    async fetchFacValData(link) {
      const apiUrl = link
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationFacVal
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersFacVal)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationFacVal.sortBy, this.paginationFacVal.sortDesc))
          if (result.data) {
            this.totalItemsFacVal = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'facValItems', data)
            } else {
              this.$set(this, 'facValItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getCommonDataList2(listName, param) {
      // Note listName is the v-model name of element object, param is the parameter
      const apiUrl = this.$store.state.commons.menu.commonDataList2
      if (apiUrl) {
        const params = {
          listName: listName,
          paramString: param
        }
        const result = await this.$http.get(expandURI(apiUrl, params))
        if (result.data.dataList) {
          this.$set(this, listName, result.data.dataList)
        }
      }
    },
    async fetchGlAcctNoData2() {
      const apiUrl = this.$store.state.commons.menu.glAcctNoList2
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationGlAcctNo2
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersGlAcctNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationGlAcctNo2.sortBy, this.paginationGlAcctNo2.sortDesc))
          if (result.data) {
            this.totalItemsGlAcctNo2 = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'glAcctNoItems2', data)
            } else {
              this.$set(this, 'glAcctNoItems2', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchGlAcctNoData3() {
      const apiUrl = this.$store.state.commons.menu.glAcctNoList3
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationGlAcctNo3
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersGlAcctNo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationGlAcctNo3.sortBy, this.paginationGlAcctNo3.sortDesc))
          if (result.data) {
            this.totalItemsGlAcctNo3 = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'glAcctNoItems3', data)
            } else {
              this.$set(this, 'glAcctNoItems3', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async valAcctPer(dataValue) {
      const apiUrl = this.$store.state.commons.menu.valAcctPer
      if (apiUrl) {
        try {
          const expand = {
            tranDate: formatDisplayDateTime2(dataValue.tranDate)
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result.data
          if (data) {
            dataValue.validAcctPer = data
          }
          // else {
          dataValue.validAcctPer = data
          // }
          return true
        } catch (err) {
          this.loading = false
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async fetchAcctPolicyData2() {
      const apiUrl = this.$store.state.commons.menu.acctPolicyList2
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationPolicy
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersPolicy)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationPolicy.sortBy, this.paginationPolicy.sortDesc))
          if (result.data) {
            this.totalItemsPol = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'acctPolicyItems', data)
            } else {
              this.$set(this, 'acctPolicyItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchIntermediaryData() {
      const apiUrl = this.$store.state.commons.menu.intermediaryList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationIntermediary
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersIntermediary)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationIntermediary.sortBy, this.paginationIntermediary.sortDesc))
          if (result.data) {
            this.totalItemsIntermediary = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'intermediaryItems', data)
            } else {
              this.$set(this, 'intermediaryItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getDataDesc(paramData, filterData) {
      const apiUrl = this.$store.state.commons.menu.getDataDesc
      if (apiUrl) {
        try {
          const expand = {
            dataName: paramData
          }
          let messageFilters = clone(filterData)
          // const result = await this.$http.get(expandURI(apiUrl, messageFilters))
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters))
          const { data } = result.data
          if (data) {
            this.doAssignDesc(data)
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async valSanction(nameValue, docNoValue, field) {
      if (this.$store.state.underwriting.init !== 1) {
        await this.$store.dispatch('underwritingInit')
      }
      if (this.$store.state.underwriting.dataTransferSettings.init !== 1) {
        await this.$store.dispatch('dataTransferInit')
      }
      const apiUrl = this.$store.state.underwriting.dataTransferSettings.menu.valSanction
      if (apiUrl) {
        try {
          if (field === 'sanctionName1Error') {
            this.sanctionName1Error = ''
          } else if (field === 'sanctionName2Error') {
            this.sanctionName2Error = ''
          } else if (field === 'sanctionName3Error') {
            this.sanctionName3Error = ''
          } else if (field === 'sanctionDocNoError') {
            this.sanctionDocNoError = ''
          }
          const expand = {
            name: nameValue,
            docNo: docNoValue
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          if (result.data) {
            const { data } = result.data
            if (data) {
              this.$set(this, 'sanctionPartialMatchItems', data)
              if (data.length > 0) {
                this.$toast.warning('There are partial matches for the name found')
              }
            } else {
              this.$set(this, 'sanctionPartialMatchItems', [])
            }
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            if (field === 'sanctionName1Error') {
              this.sanctionName1Error = data.messages[0].message
            } else if (field === 'sanctionName2Error') {
              this.sanctionName2Error = data.messages[0].message
            } else if (field === 'sanctionName3Error') {
              this.sanctionName3Error = data.messages[0].message
            } else if (field === 'sanctionDocNoError') {
              this.sanctionDocNoError = data.messages[0].message
            }
          }
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async valSanctionClmPayt(clmNoValue, nameValue, docNoValue, field) {
      if (this.$store.state.underwriting.init !== 1) {
        await this.$store.dispatch('underwritingInit')
      }
      if (this.$store.state.underwriting.dataTransferSettings.init !== 1) {
        await this.$store.dispatch('dataTransferInit')
      }
      const apiUrl = this.$store.state.underwriting.dataTransferSettings.menu.valSanctionClmPayt
      if (apiUrl) {
        try {
          if (field === 'sanctionName1Error') {
            this.sanctionName1Error = ''
          } else if (field === 'sanctionName2Error') {
            this.sanctionName2Error = ''
          } else if (field === 'sanctionName3Error') {
            this.sanctionName3Error = ''
          } else if (field === 'sanctionDocNoError') {
            this.sanctionDocNoError = ''
          }
          const expand = {
            clmNo: clmNoValue,
            name: nameValue,
            docNo: docNoValue
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          if (result.data) {
            const { data } = result.data
            if (data) {
              this.$set(this, 'sanctionPartialMatchItems', data)
              if (data.length > 0) {
                this.$toast.warning('There are partial matches for the name found')
              }
            } else {
              this.$set(this, 'sanctionPartialMatchItems', [])
            }
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            if (field === 'sanctionName1Error') {
              this.sanctionName1Error = data.messages[0].message
            } else if (field === 'sanctionName2Error') {
              this.sanctionName2Error = data.messages[0].message
            } else if (field === 'sanctionName3Error') {
              this.sanctionName3Error = data.messages[0].message
            } else if (field === 'sanctionDocNoError') {
              this.sanctionDocNoError = data.messages[0].message
            }
          }
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async fetchGLAnCodeData() {
      const apiUrl = this.$store.state.commons.menu.glAnCodeList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationGlAnCode
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersGlAnCode)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationGlAnCode.sortBy, this.paginationGlAnCode.sortDesc))
          if (result.data) {
            this.totalItemsGlAnCode = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'glAnCodeItems', data)
            } else {
              this.$set(this, 'glAnCodeItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchBusClsData() {
      const apiUrl = this.$store.state.commons.menu.busClsList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationBusCls
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersBusCls)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationBusCls.sortBy, this.paginationBusCls.sortDesc))
          if (result.data) {
            this.totalItems = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'busClsItems', data)
            } else {
              this.$set(this, 'busClsItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchUserData() {
      const apiUrl = this.$store.state.commons.menu.userList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationUser
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersUser)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationUser.sortBy, this.paginationUser.sortDesc))
          if (result.data) {
            this.totalItems = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'userItems', data)
            } else {
              this.$set(this, 'userItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchAgencyData() {
      const apiUrl = this.$store.state.commons.menu.getAgencyList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationAgency
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersAgency)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationAgency.sortBy, this.paginationAgency.sortDesc))
          if (result.data) {
            this.totalItems = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'agencyItems', data)
            } else {
              this.$set(this, 'agencyItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    resetSanction() {
      this.sanctionName1Error = ''
      this.sanctionName2Error = ''
      this.sanctionName3Error = ''
      this.sanctionDocNoError = ''
      this.sanctionPartialMatchItems = []
      this.showSanctionDialog = false
    },
    async fetchCoverNoteData() {
      const apiUrl = this.$store.state.commons.menu.coverNoteList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationCoverNote
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersCoverNote)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationCoverNote.sortBy, this.paginationCoverNote.sortDesc))
          if (result.data) {
            this.totalItemsCoverNote = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'coverNoteItems', data)
            } else {
              this.$set(this, 'coverNoteItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchVehRegData() {
      const apiUrl = this.$store.state.commons.menu.vehRegList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationVehReg
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersVehReg)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationVehReg.sortBy, this.paginationVehReg.sortDesc))
          if (result.data) {
            this.totalItemsVehReg = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'vehRegItems', data)
            } else {
              this.$set(this, 'vehRegItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchAddress(postalCode) {
      const apiUrl = this.$store.state.commons.menu.address
      if (apiUrl) {
        try {
          const expand = {
            postCode: postalCode
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            this.assignAddress(data)
          }
          this.loading = false
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.notify(data)
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchAcctPeriodData() {
      const apiUrl = this.$store.state.commons.menu.acctPeriodLists
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationAcctPeriod
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersAcctPeriod)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationAcctPeriod.sortBy, this.paginationAcctPeriod.sortDesc))
          if (result.data) {
            this.totalItemsAcctPeriod = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'acctPeriodItems', data)
            } else {
              this.$set(this, 'acctPeriodItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchAcctPeriodData2() {
      const apiUrl = this.$store.state.commons.menu.acctPeriodLists2
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationAcctPeriod
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersAcctPeriod)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationAcctPeriod.sortBy, this.paginationAcctPeriod.sortDesc))
          if (result.data) {
            this.totalItemsAcctPeriod = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'acctPeriodItems', data)
            } else {
              this.$set(this, 'acctPeriodItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchPolicyData2() {
      const apiUrl = this.$store.state.commons.menu.policyList3
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationPolicy
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersPolicy)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationPolicy.sortBy, this.paginationPolicy.sortDesc))
          if (result.data) {
            this.totalItemsPol = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'polItems', data)
            } else {
              this.$set(this, 'polItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    // 2021-01-05 OS-624
    async fetchOpenCoverMasterPolicyData() {
      const apiUrl = this.$store.state.commons.menu.openCoverMasterPolicyList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationPolicy
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersOpenCoverMasterPolicy)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationPolicy.sortBy, this.paginationPolicy.sortDesc))
          if (result.data) {
            this.totalItemsPol = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'polItems', data)
            } else {
              this.$set(this, 'polItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    // 2021-01-14 Intermediary by InterGrp = 11
    async fetchIntermediaryBrokerData() {
      const apiUrl = this.$store.state.commons.menu.intermediaryBrokerList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationIntermediary
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersIntermediary)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationIntermediary.sortBy, this.paginationIntermediary.sortDesc))
          if (result.data) {
            this.totalItemsIntermediary = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'intermediaryItems', data)
            } else {
              this.$set(this, 'intermediaryItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchIntermediaryCompanyData() {
      const apiUrl = this.$store.state.commons.menu.intermediaryCompanyList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationIntermediary
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersIntermediary)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationIntermediary.sortBy, this.paginationIntermediary.sortDesc))
          if (result.data) {
            this.totalItemsIntermediary = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'intermediaryItems', data)
            } else {
              this.$set(this, 'intermediaryItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    // async fetchTreatyDetData() {
    //   const apiUrl = this.$store.state.commons.menu.intermediaryCompanyList
    //   if (apiUrl) {
    //     try {
    //       this.loading = true
    //       const { page, itemsPerPage } = this.paginationTreatyDet
    //       const expand = {
    //         page: page - 1,
    //         size: itemsPerPage
    //       }
    //       let messageFilters = clone(this.filtersTreatyDet)
    //       const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationTreatyDet.sortBy, this.paginationTreatyDet.sortDesc))
    //       if (result.data) {
    //         this.totalItemsTreatyDet = result.data.totalElements
    //         const { data } = result.data
    //         if (data) {
    //           this.$set(this, 'intermediaryItems', data)
    //         } else {
    //           this.$set(this, 'intermediaryItems', [])
    //         }
    //       }
    //       this.loading = false
    //     } catch (err) {
    //       const { data } = err.response
    //       if (data) {
    //         this.message(data)
    //       }
    //       this.loading = false
    //     }
    //   } else {
    //     this.errorMessage('The request failed because it is trying to execute an invalid API url.')
    //   }
    // },
    async valAcctNo(acctNo) {
      const apiUrl = this.$store.state.commons.menu.valAcctNo
      if (apiUrl) {
        try {
          const expand = {
            acctNo: acctNo
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result.data
          this.isValAcctNo = data
          return true
        } catch (err) {
          this.loading = false
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    insideValidateIntermediary(messages) {
      let msges = []
      if (messages) {
        this.errMsg = ''
        let hasError = false
        messages.forEach(msg => {
          if (msg.type === 'ERROR') {
            hasError = true
          }
          msges.push(msg.message)
        })
        if (msges.length > 1) {
          this.errMsg = msges.join('\n')
        } else {
          this.errMsg = msges[0]
        }

        if (hasError === true) {
          this.isWarning = false
          this.acceptInterWarn = false
        } else {
          this.isWarning = true
        }

        if (this.acceptInterWarn === false) {
          this.promptMsg = true
        }
      }
    },
    async validateIntermediary(tranDate, accountNo) {
      const apiUrl = this.$store.state.commons.menu.validateIntermediary
      if (apiUrl) {
        try {
          const params = {
            acctNo: accountNo,
            tranDate: formatDisplayDateTime2(tranDate)
          }
          const result = await this.$http.get(expandURI(apiUrl, params))
          const { messages } = result.data
          this.insideValidateIntermediary(messages)
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.loading = false
          this.promptMsg = false
        }
      } else {
        this.promptMsg = false
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async valIntermediary(tranDate, accountNo) {
      const apiUrl = this.$store.state.commons.menu.getValidIntermediary
      if (apiUrl) {
        try {
          const params = {
            acctNo: accountNo,
            tranDate: formatDisplayDateTime2(tranDate)
          }
          const result = await this.$http.get(expandURI(apiUrl, params))
          const { data } = result.data
          if (data) {
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.loading = false
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async fetchPolStatData() {
      const apiUrl = this.$store.state.commons.menu.polStatList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationPolStat
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersPolStat)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationPolStat.sortBy, this.paginationPolStat.sortDesc))
          if (result.data) {
            this.totalItemsPolStat = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'polStatItems', data)
            } else {
              this.$set(this, 'polStatItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchCargoComdtyData() {
      const apiUrl = this.$store.state.commons.menu.cargoCommodityList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationCargoComdty
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersCargoComdty)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationCargoComdty.sortBy, this.paginationCargoComdty.sortDesc))
          if (result.data) {
            this.totalItemsCargoComdty = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'cargoComdtyItems', data)
            } else {
              this.$set(this, 'cargoComdtyItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchCargoPackData() {
      const apiUrl = this.$store.state.commons.menu.cargoPackingList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationCargoPack
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let messageFilters = clone(this.filtersCargoPack)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), messageFilters, this.paginationCargoPack.sortBy, this.paginationCargoPack.sortDesc))
          if (result.data) {
            this.totalItemsCargoPack = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'cargoPackItems', data)
            } else {
              this.$set(this, 'cargoPackItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchPolicyRenewalData() {
      const apiUrl = this.$store.state.commons.menu.policyRenewalList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationPolicyRenewal
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersPolicy)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationPolicyRenewal.sortBy, this.paginationPolicyRenewal.sortDesc))
          if (result.data) {
            this.totalItemsPol = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'polItems', data)
            } else {
              this.$set(this, 'polItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getDisplayPrinter() {
      const apiUrl = this.$store.state.commons.menu.displayPrinter
      if (apiUrl) {
        const result = await this.$http.get(apiUrl)
        if (result.data.dataMap.displayPrinter) {
          this.$set(this, 'displayPrinter', result.data.dataMap.displayPrinter)
        }
      }
    },
    async fetchRiOutwardsHelpListData() {
      const apiUrl = this.$store.state.commons.menu.riOutwardsList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationRi
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersRi)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationRi.sortBy, this.paginationRi.sortDesc))
          if (result.data) {
            this.totalItemsRi = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'riItems', data)
            } else {
              this.$set(this, 'riItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getAcctPer(fieldName, modNo, tranDate) {
      const apiUrl = this.$store.state.commons.menu.getAcctPer
      if (apiUrl) {
        try {
          const params = {
            modNo: modNo,
            tranDate: tranDate
          }
          const result = await this.$http.get(expandURI(apiUrl, params))
          const { dataMap } = result.data
          if (dataMap) {
            this.$set(this, fieldName, dataMap)
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.loading = false
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async fetchPolRiskGrpData() {
      const apiUrl = this.$store.state.commons.menu.polRiskGrpList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationPolRiskGrp
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersPolRiskGrp)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationPolRiskGrp.sortBy, this.paginationPolRiskGrp.sortDesc))
          if (result.data) {
            this.totalItemsPolRiskGrp = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'polRiskGrpItems', data)
            } else {
              this.$set(this, 'polRiskGrpItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchPolRiskData() {
      const apiUrl = this.$store.state.commons.menu.polRiskList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationPolRisk
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersPolRisk)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationPolRisk.sortBy, this.paginationPolRisk.sortDesc))
          if (result.data) {
            this.totalItemsPolRisk = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'polRiskItems', data)
            } else {
              this.$set(this, 'polRiskItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchPolItemData() {
      const apiUrl = this.$store.state.commons.menu.polItemList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationPolItem
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersPolItem)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationPolItem.sortBy, this.paginationPolItem.sortDesc))
          if (result.data) {
            this.totalItemsPolItem = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'polItemItems', data)
            } else {
              this.$set(this, 'polItemItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchRiCo() {
      const apiUrl = this.$store.state.commons.menu.getRiCoList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationRiCo
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersRiCo)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationRiCo.sortBy, this.paginationRiCo.sortDesc))
          if (result.data) {
            this.totalItems = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'riCoItems', data)
            } else {
              this.$set(this, 'riCoItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchGstRateData() {
      const apiUrl = this.$store.state.commons.menu.gstRateList
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.paginationGstRate
          const expand = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.filtersGstRate)
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, expand), massageFilters, this.paginationGstRate.sortBy, this.paginationGstRate.sortDesc))
          if (result.data) {
            this.totalItemsGstRate = result.data.totalElements
            const { data } = result.data
            if (data) {
              this.$set(this, 'gstRateItems', data)
            } else {
              this.$set(this, 'gstRateItems', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    }
  },
  mixins: [
    MessageMixin
  ]
}
